import { useEffect, useRef } from 'react';
import { talentSeesAdminShifts } from './useHygienistCalendarData';

// T2-7436: job events reach every open talent app. Each app used to refetch
// Home 500 ms after any of them, so one booking made every open app hit the
// API in the same second ("reload herd"). Talent now refresh only for their own
// shifts and offers, or for a new shift they could take, and each app waits a
// random delay so the refreshes spread out.
const OWN_JOB_REFRESH_MIN_MS = 250;
const OWN_JOB_REFRESH_JITTER_MS = 1250;
const NEW_SHIFT_REFRESH_MIN_MS = 250;
const NEW_SHIFT_REFRESH_JITTER_MS = 4750;

const JOB_EVENTS = ['jobBooked', 'jobUpdated', 'jobCancelled', 'offerRejected', 'instabookUpdater'];
/** After these, nobody else can take the shift any more. */
const SHIFT_GONE_EVENTS = ['jobBooked', 'jobCancelled'];

interface UseHygienistSocketEventsParams {
  onRefresh: () => Promise<void> | void;
  /** The talent's own shift, offer or InstaBook request. Without it every job counts. */
  isRelevantJob?: (jobId: number) => boolean;
  /** A shift that is only in the talent's available list. */
  isAvailableOnlyJob?: (jobId: number) => boolean;
  /** Takes a shift someone else got off the available list, without a refetch. */
  onJobUnavailable?: (jobId: number) => void;
}

const currentUser = () => (window as any)?.TempStars?.User?.getCurrentUser?.() ?? null;

const toJobId = (payload?: any): number | null => {
  const jobId = Number(payload?.jobId);
  return Number.isInteger(jobId) && jobId > 0 ? jobId : null;
};

/**
 * Same rule as the server's shift list: hygienists see type 0 shifts, every
 * other talent type 1, Canadian assistants who agreed to admin work also see
 * type 2, and only shifts in the talent's country.
 */
export function isNewShiftForTalent(payload: any, hygienist: any): boolean {
  if (!hygienist) return false;
  const talentType = Number(hygienist.type);
  if (!Number.isFinite(talentType)) return false;
  const shiftType = Number(payload?.type);
  const typeMatches = shiftType === (talentType === 0 ? 0 : 1)
    || (shiftType === 2 && talentSeesAdminShifts(hygienist));
  if (!typeMatches) return false;
  if (payload?.country == null || hygienist.country == null) return true;
  return Number(payload.country) === Number(hygienist.country);
}

/**
 * Subscribe to legacy socket events and refresh Home for the ones that concern
 * this talent.
 */
export default function useHygienistSocketEvents({
  onRefresh,
  isRelevantJob,
  isAvailableOnlyJob,
  onJobUnavailable,
}: UseHygienistSocketEventsParams) {
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;
  const isRelevantJobRef = useRef(isRelevantJob);
  isRelevantJobRef.current = isRelevantJob;
  const isAvailableOnlyJobRef = useRef(isAvailableOnlyJob);
  isAvailableOnlyJobRef.current = isAvailableOnlyJob;
  const onJobUnavailableRef = useRef(onJobUnavailable);
  onJobUnavailableRef.current = onJobUnavailable;

  useEffect(() => {
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    let refreshDueAt = 0;

    // One refresh per burst; an event that needs a sooner refresh moves it earlier.
    const scheduleRefresh = (minMs: number, jitterMs: number) => {
      const delay = minMs + Math.floor(Math.random() * jitterMs);
      const dueAt = Date.now() + delay;
      if (refreshTimer && refreshDueAt <= dueAt) return;
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshDueAt = dueAt;
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        Promise.resolve()
          .then(() => refreshRef.current?.())
          .catch(() => undefined);
      }, delay);
    };

    const isTalent = () => !!currentUser()?.hygienistId;

    const handleNewJobPosted = (payload: any) => {
      try {
        if (!isTalent()) return;
        if (isNewShiftForTalent(payload, currentUser()?.hygienist)) {
          scheduleRefresh(NEW_SHIFT_REFRESH_MIN_MS, NEW_SHIFT_REFRESH_JITTER_MS);
        }
      } catch (e) {
        // noop
      }
    };

    const hideUnreadBadgeIfPossible = (payload?: any) => {
      try {
        const jobs = (window as any)?.TempStars?.Hygienist?.jobData?.jobs || [];
        const target = payload?.jobId;
        if (!target) return;
        const job = jobs.find((j: any) => j?.id === target);
        const startDate = job?.startDate;
        if (!startDate) return;
        // Convert to legacy YYYY-M-D token and hide unread badge for that date pre-refresh
        const dt = new Date(startDate);
        const token = `${dt.getUTCFullYear()}-${dt.getUTCMonth()}-${dt.getUTCDate()}`;
        const el = document.querySelector(`[data-date="${token}"] .unread-message-count`) as HTMLElement | null;
        if (el) el.style.display = 'none';
      } catch {}
    };

    const dropIfAvailableOnly = (jobId: number) => {
      if (isAvailableOnlyJobRef.current?.(jobId)) onJobUnavailableRef.current?.(jobId);
    };

    const jobEventHandlers: Record<string, (payload?: any) => void> = {};
    JOB_EVENTS.forEach((event) => {
      jobEventHandlers[event] = (payload?: any) => {
        try {
          if (!isTalent()) return;
          const jobId = toJobId(payload);
          const isRelevant = isRelevantJobRef.current;
          // Unknown job ids fail open: refreshing is always safe.
          if (payload?.targeted === true || jobId === null || !isRelevant || isRelevant(jobId)) {
            hideUnreadBadgeIfPossible(payload);
            scheduleRefresh(OWN_JOB_REFRESH_MIN_MS, OWN_JOB_REFRESH_JITTER_MS);
            return;
          }
          // Someone else's shift: at most drop it from the available list.
          if (SHIFT_GONE_EVENTS.includes(event)) dropIfAvailableOnly(jobId);
        } catch (e) {
          // noop
        }
      };
    });

    // Sent once the server tells everyone a shift was taken (the 5.28 targeting work).
    const handleShiftUnavailable = (payload?: any) => {
      try {
        if (!isTalent()) return;
        const jobId = toJobId(payload);
        if (jobId !== null) dropIfAvailableOnly(jobId);
      } catch (e) {
        // noop
      }
    };

    const handlers: Array<[string, (payload?: any) => void]> = [
      ['newJobPosted', handleNewJobPosted],
      ...Object.entries(jobEventHandlers),
      ['shiftUnavailable', handleShiftUnavailable],
    ];

    let boundSocket: any = null;

    const unbindFrom = (sock: any) => {
      if (!sock || typeof sock.off !== 'function') return;
      try {
        handlers.forEach(([event, handler]) => sock.off(event, handler));
      } catch (e) {
        // noop
      }
    };

    // The shell replaces TempStars.socket when it reconnects (resume, new token).
    const bindToCurrentSocket = () => {
      const sock = (window as any)?.TempStars?.socket;
      if (!sock || typeof sock.on !== 'function' || sock === boundSocket) return;
      unbindFrom(boundSocket);
      handlers.forEach(([event, handler]) => sock.on(event, handler));
      boundSocket = sock;
    };

    bindToCurrentSocket();

    const onVisible = () => {
      if (document.visibilityState === 'visible') bindToCurrentSocket();
    };

    document.addEventListener('tempstars:socket-ready', bindToCurrentSocket);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      document.removeEventListener('tempstars:socket-ready', bindToCurrentSocket);
      document.removeEventListener('visibilitychange', onVisible);
      unbindFrom(boundSocket);
    };
  }, []);
}
